import { useRef, useState } from 'react';
import { employeesService, type EmployeeShiftAccessForm, type UpdateEmployeeRequest } from '../../../services/employees/employees.service';

export function useEmployeeShiftAccess(employeeSession: string | null, onError: (message: string) => void) {
  const [shiftAccessForm, setForm] = useState<EmployeeShiftAccessForm | null>(null);
  const [baseline, setBaseline] = useState<EmployeeShiftAccessForm | null>(null);
  const [isLoadingShiftAccess, setLoading] = useState(false);
  const requestRef = useRef(0);

  const resetShiftAccess = () => {
    requestRef.current += 1;
    setForm(null);
    setBaseline(null);
    setLoading(false);
  };

  const loadShiftAccess = async (employeeId: number) => {
    const requestId = ++requestRef.current;
    setForm(null);
    setBaseline(null);
    setLoading(true);
    try {
      if (!employeeSession) return;
      const access = await employeesService.getShiftAccess(employeeId, employeeSession);
      if (requestRef.current !== requestId) return;
      const form: EmployeeShiftAccessForm = {
        hasShiftAccess: access.hasShiftAccess,
        enabled: access.shiftIsActive,
        username: access.shiftUsername ?? '',
        password: '',
        confirmPassword: '',
        role: access.shiftRole ?? 'worker',
        canEditEmployees: access.canEditEmployees,
        canAssignEmployees: access.canAssignEmployees,
        canManageSchedule: access.canManageSchedule,
        canCreateEmployees: access.canCreateEmployees,
      };
      setForm(form);
      setBaseline(form);
    } catch (error) {
      if (requestRef.current === requestId) onError(error instanceof Error ? error.message : 'טעינת גישת SHIFT נכשלה');
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  };

  const onChangeShiftAccess = <Key extends keyof EmployeeShiftAccessForm>(key: Key, value: EmployeeShiftAccessForm[Key]) => {
    setForm((previous) => previous ? {
      ...previous,
      ...(key === 'role' ? { canEditEmployees: false, canAssignEmployees: false, canManageSchedule: false, canCreateEmployees: false } : {}),
      [key]: value,
    } : previous);
  };

  const buildShiftAccessUpdate = (): UpdateEmployeeRequest['shiftAccess'] => {
    if (!shiftAccessForm || !baseline || JSON.stringify(shiftAccessForm) === JSON.stringify(baseline)) return undefined;
    const form = shiftAccessForm;
    return {
      enabled: form.enabled,
      username: form.username.trim(),
      role: form.role,
      canEditEmployees: form.role === 'manager' && form.canEditEmployees,
      canAssignEmployees: form.role === 'manager' && form.canAssignEmployees,
      canManageSchedule: form.role === 'manager' && form.canManageSchedule,
      canCreateEmployees: form.role === 'manager' && form.canCreateEmployees,
      ...(form.password ? { password: form.password, confirmPassword: form.confirmPassword } : {}),
    };
  };

  return { shiftAccessForm, isLoadingShiftAccess, loadShiftAccess, resetShiftAccess, onChangeShiftAccess, buildShiftAccessUpdate };
}